import dayjs from "dayjs";
import { isEquitySellType } from "../../../utils/reports/equityTransactionTypes";
import { formatReportTypeColumnLabel } from "../../../utils/reports/reportDisplayLabels";
import { withTxnBenchmarkFields } from "../../../utils/reports/transactionBenchmarkColumns";
import { withTxnMetadataFields } from "../../../utils/reports/transactionMetadataColumns";
import {
  attachInvestorMeta,
  buildUserMetaFromInvestors,
  type UserMeta,
} from "../mf/mfReportParsing";
import { SIF_POINT_TO_POINT_REPORT, SIF_RETURNS_REPORT } from "./sifReportConfig";

export { attachInvestorMeta, buildUserMetaFromInvestors, type UserMeta };

const formatEpochDate = (value: unknown): string => {
  if (value == null || value === "" || value === "-") return "-";
  const n = Number(value);
  if (Number.isFinite(n) && n > 0) {
    return dayjs(n).format("DD/MM/YYYY");
  }
  const asStr = String(value).trim();
  if (/^\d{2}\/\d{2}\/\d{4}$/.test(asStr)) return asStr;
  const parsed = dayjs(asStr);
  if (parsed.isValid()) return parsed.format("DD/MM/YYYY");
  return String(value);
};

const resolveCurrentDateOrToday = (row: Record<string, any>): string => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  const raw = row.currentDate ?? meta.currentDate;
  if (raw == null || raw === "" || raw === "-" || raw === 0 || raw === "0") {
    return dayjs().format("DD/MM/YYYY");
  }
  const formatted = formatEpochDate(raw);
  return formatted === "-" ? dayjs().format("DD/MM/YYYY") : formatted;
};

const isP2pSellRow = (row: Record<string, any>): boolean => {
  if (
    row.sellingAmount != null ||
    row.sellingNav != null ||
    row.sellingNAV != null ||
    row.redemptionAmount != null ||
    row.redeemAmount != null ||
    row.redeemValue != null ||
    row.redemptionValue != null ||
    row.netRedeem != null
  ) {
    return true;
  }
  return isSellTxn(row);
};

const mapSifP2pMetricFields = (
  row: Record<string, any>,
  isSell: boolean,
): Record<string, any> => {
  const unrealPnlRaw =
    row.unrealisedPNL ?? row.unrealisedPnL ?? row.unrealizedPNL;
  const unrealPercRaw =
    row.unrealisedPNLPercentage ??
    row.unrealisedPnLPercentage ??
    row.unrealizedPNLPercentage;
  const realisedPnlRaw =
    row.realisedPnl ?? row.realisedPNL ?? row.realizedPnl ?? row.realizedPNL;
  const realisedPercRaw =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage;

  const nav = isSell
    ? (row.sellingNav ??
      row.sellingNAV ??
      row.redemptionNAV ??
      row.currentNAV ??
      row.currentNav)
    : (row.currentNAV ??
      row.currentNav ??
      row.purchaseNAV ??
      row.purchaseNav ??
      row.nav ??
      row.avgNAV ??
      row.avgPrice);

  const invested = isSell
    ? "-"
    : (row.investedAmount ??
      row.netPurchase ??
      row.purchaseAmount ??
      row.amount ??
      row.costValue);

  return {
    type: formatReportTypeColumnLabel(
      isSell ? "Realized" : "Unrealized",
      isSell ? "Realized" : "Unrealized",
    ),
    displayTransType: isSell ? "Realized" : "Unrealized",
    displayTransactionType: isSell ? "Realized" : "Unrealized",
    units: row.units ?? row.closingBalance ?? row.quantity,
    currentNAV: nav,
    avgPrice:
      row.avgPrice ??
      row.purchaseNAV ??
      row.purchaseNav ??
      row.avgNAV ??
      row.nav ??
      "-",
    xirr: row.xirr ?? "-",
    cagr: row.cagr ?? "-",
    investedAmount: invested,
    currentAmount: isSell
      ? "-"
      : (row.currentAmount ?? row.currentValue ?? row.marketValue ?? "-"),
    currentValue: isSell
      ? "-"
      : (row.currentAmount ?? row.currentValue ?? row.marketValue ?? "-"),
    unrealisedPNL: isSell ? "-" : (unrealPnlRaw ?? "-"),
    unrealisedPNLPercentage: isSell ? "-" : (unrealPercRaw ?? "-"),
    realisedPnl: isSell ? (realisedPnlRaw ?? "-") : "-",
    realisedPNL: isSell ? (realisedPnlRaw ?? "-") : "-",
    realisedPNLPercentage: isSell ? (realisedPercRaw ?? "-") : "-",
  };
};

const mapSifP2pReportRow = (row: Record<string, any>): Record<string, any> => {
  const isSell = isP2pSellRow(row);
  const {
    transType: _transType,
    txnType: _txnType,
    transactionType: _transactionType,
    type: _legacyType,
    ...rest
  } = row;
  const purchaseRaw = row.purchaseDate ?? row.saleDate;
  const purchaseDateFormatted =
    purchaseRaw == null || purchaseRaw === "" || purchaseRaw === "-"
      ? "-"
      : formatEpochDate(purchaseRaw);
  const daysNum = Number(row.days);
  const hasValidDays =
    row.days != null &&
    row.days !== "" &&
    row.days !== "-" &&
    Number.isFinite(daysNum);
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...rest,
      schemeName: row.schemeName ?? row.name ?? row.scheme_name,
      ...mapSifP2pMetricFields(row, isSell),
      purchaseDate: purchaseDateFormatted,
      currentDate: resolveCurrentDateOrToday(row),
      days: hasValidDays ? daysNum : "-",
      folioNo: row.folioNo ?? row.folioNumber ?? row.folio,
      memberName: row.memberName,
      userId: row.userId,
    }),
  );
};

const extractSifP2pRows = (payload: any): any[] => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.data?.data)) return payload.data.data;
  if (Array.isArray(payload.response)) return payload.response;
  if (Array.isArray(payload.rows)) return payload.rows;
  return [];
};

const isSellTxn = (row: Record<string, any>): boolean => {
  const raw = String(
    row.transType ??
      row.txnType ??
      row.transactionType ??
      row.transactiontype ??
      "",
  )
    .trim()
    .replace(/\s+/g, "_")
    .toUpperCase();
  if (!raw) return false;
  return (
    isEquitySellType(raw) ||
    raw === "SELL" ||
    raw === "REDEMPTION" ||
    raw === "REDEEM"
  );
};

const filterPositiveUnits = (rows: any[]) =>
  rows.filter((r) => Number(r?.units ?? r?.quantity ?? 0) > 0);

const filterRealizedTxnRows = (rows: any[]) =>
  rows.filter((r) => isSellTxn(r)).map((r) => ({
    ...r,
    type: formatReportTypeColumnLabel("Realized", "Realized"),
    displayTransType: "Realized",
  }));

const resolveHoldingCagr = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  for (const key of ["cagr", "cagrSif"] as const) {
    const val = row[key] ?? meta[key];
    if (val != null && val !== "") return val;
  }
  return row.cagr ?? "-";
};

const getHoldingIsin = (
  h: Record<string, any>,
  hMeta?: Record<string, any>,
): string => {
  const meta = hMeta ?? h?.metadata ?? h?.meta ?? h?.metaData ?? {};
  const val =
    h?.isin ??
    h?.ISIN ??
    h?.schemeCode ??
    meta.isin ??
    meta.ISIN ??
    meta.schemeCode;
  if (val == null || val === "") return "-";
  return String(val);
};

const resolveHoldingAverageDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  const val =
    row.averageDays ??
    row.averageDay ??
    row.days ??
    row.totalTimeInDays ??
    meta.averageDays ??
    meta.averageDay ??
    meta.days;
  if (val == null || val === "") return "-";
  return val;
};

const resolveTxnDays = (row: Record<string, any>): string | number => {
  const meta = row?.metadata ?? row?.meta ?? row?.metaData ?? {};
  const val = row.days ?? row.avgDays ?? meta.days;
  if (val == null || val === "") return "-";
  return val;
};

const formatCombinedPnlDisplay = (
  amountRaw: unknown,
  percentRaw: unknown,
): string => {
  const toNum = (v: unknown): number | null => {
    if (v == null || v === "" || v === "-") return null;
    const n = typeof v === "number" ? v : Number(String(v).replace(/,/g, ""));
    return Number.isFinite(n) ? n : null;
  };
  const amount = toNum(amountRaw);
  const percent = toNum(percentRaw);
  if (amount == null && percent == null) return "-";
  if ((amount ?? 0) === 0 && (percent ?? 0) === 0) return "-";
  const amountSign = (amount ?? 0) > 0 ? "+" : (amount ?? 0) < 0 ? "-" : "";
  const percentSign = (percent ?? 0) > 0 ? "+" : (percent ?? 0) < 0 ? "-" : "";
  const amountPart =
    amount != null
      ? `${amountSign}₹${Math.abs(amount).toLocaleString("en-IN", {
          maximumFractionDigits: 2,
        })}`
      : "-";
  const percentPart =
    percent != null
      ? `${percentSign}${Math.abs(percent).toLocaleString("en-IN", {
          maximumFractionDigits: 2,
        })}%`
      : "-";
  return `${amountPart} / ${percentPart}`;
};

const normalizeSifReportTotalHoldingInfo = (
  info: Record<string, unknown> | null | undefined,
): Record<string, unknown> | null => {
  if (!info || typeof info !== "object" || Object.keys(info).length === 0) {
    return null;
  }
  return {
    ...info,
    totalValueInvested:
      info.totalValueInvested ?? info.totalInvested ?? info.investedAmount,
    totalInvested:
      info.totalInvested ?? info.totalValueInvested ?? info.investedAmount,
    currentValue: info.currentValue ?? info.currentAmount,
    unrealisedGainLoss: info.unrealisedGainLoss ?? info.gainLoss,
    gainLoss: info.gainLoss ?? info.unrealisedGainLoss,
    gainLossPercentage:
      info.gainLossPercentage ?? info.gainLossPerc ?? info.roi,
    cagrSif: info.cagrSif ?? info.cagr,
    cagr: info.cagr ?? info.cagrSif,
  };
};

const getHoldingTransactionList = (holding: Record<string, any>): any[] => {
  const list =
    holding.transactions ??
    holding.transaction ??
    holding.txns ??
    holding.trxn ??
    [];
  return Array.isArray(list) ? list : [];
};

const normalizeUnrealizedHoldingRow = (row: Record<string, any>) => {
  const meta = row?.metadata ?? row?.meta ?? {};
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName: row.schemeName ?? row.name ?? "-",
      type: formatReportTypeColumnLabel("Unrealized", "Unrealized"),
      isin: getHoldingIsin(row, meta),
      units: row.units ?? row.quantity,
      currentPrice: row.currentPrice ?? row.currentNAV ?? row.currentNav,
      investedAmount: row.investedAmount ?? row.amount ?? row.costValue,
      currentAmount: row.currentAmount ?? row.currentValue,
      gainLoss:
        row.gainLoss ??
        row.unrealisedPNL ??
        row.unrealisedPnL ??
        row.unrealizedPNL,
      gainLossPercentage:
        row.gainLossPercentage ??
        row.gainLossPerc ??
        row.unrealisedPNLPercentage,
      folioNumber: row.folioNumber ?? row.folioNo ?? row.folio,
      cagr: resolveHoldingCagr(row),
      days: resolveHoldingAverageDays(row),
      xirr: row.xirr ?? meta.xirr ?? "-",
      adjustedCostPrice:
        row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
    }),
  );
};

const normalizeRealizedRow = (row: Record<string, any>) => {
  const meta = row.metadata ?? row.meta ?? {};
  const avgBuy =
    row.avgBuyPrice ??
    row.averageBuyPrice ??
    row.averagePrice ??
    row.purchasePrice ??
    meta.avgBuyPrice;
  const sellNav =
    row.purchaseNAV ??
    row.purchaseNav ??
    row.currentNAV ??
    row.currentNav ??
    row.nav;
  return withTxnMetadataFields(
    withTxnBenchmarkFields({
      ...row,
      schemeName: row.schemeName ?? row.name ?? "-",
      isin: getHoldingIsin(row, meta),
      type: formatReportTypeColumnLabel("Realized", "Realized"),
      transactionDate: formatEpochDate(
        row.transactionDate ?? row.transactiondate ?? row.date,
      ),
      purchaseNAV: sellNav ?? avgBuy ?? "-",
      avgBuyPrice: avgBuy ?? "-",
      saleValue: row.saleValue ?? row.amount ?? row.currentAmount,
      units: row.units ?? row.quantity,
      realisedPnl:
        row.realisedPnl ??
        row.realisedPnL ??
        row.realisedPNL ??
        row.realizedPnl ??
        row.gainLoss,
      realisedPNLPercentage:
        row.realisedPNLPercentage ??
        row.realisedPnLPercentage ??
        row.realizedPNLPercentage ??
        row.gainLossPercentage,
      cagr: resolveHoldingCagr(row),
      gainLoss: undefined,
    }),
  );
};

const flattenSifHoldingsTransactions = (holdings: any[]): any[] => {
  const txns: any[] = [];
  for (const h of holdings || []) {
    if (!h || typeof h !== "object") continue;
    const schemeName = h.schemeName ?? h.name ?? "-";
    const list = getHoldingTransactionList(h);
    if (!list.length) continue;
    for (const t of list) {
      const isSell = isSellTxn(t);
      const unrealPnl =
        t.unrealisedPNL ?? t.unrealisedPnL ?? t.unrealisedGainLoss;
      const unrealPct =
        t.unrealisedPNLPercentage ?? t.unrealisedPnLPercentage;
      const realisedPnl =
        t.realisedPnl ?? t.realisedPnL ?? t.realisedPNL;
      const realisedPct =
        t.realisedPNLPercentage ?? t.realisedPnLPercentage;
      txns.push(
        withTxnMetadataFields(
          withTxnBenchmarkFields({
            ...t,
            schemeName: t.schemeName ?? schemeName,
            isin: getHoldingIsin(t, t.metadata ?? t.meta) !== "-"
              ? getHoldingIsin(t, t.metadata ?? t.meta)
              : getHoldingIsin(h),
            folioNumber: t.folioNumber ?? h.folioNumber ?? h.folioNo,
            userId: t.userId ?? h.userId,
            memberName: t.memberName ?? h.memberName,
            transactionDate: formatEpochDate(
              t.transactionDate ?? t.transactiondate ?? t.date,
            ),
            transType: t.transType ?? t.txnType ?? t.transactionType,
            purchasePrice:
              t.purchasePrice ?? t.purchaseNAV ?? t.price ?? t.averagePrice,
            currentPrice: t.currentPrice ?? t.currentNAV ?? h.currentPrice,
            amount: t.amount ?? t.costValue ?? t.investedAmount,
            currentAmount: t.currentAmount ?? t.currentValue ?? h.currentAmount,
            units: t.units ?? t.quantity,
            unrealisedPNLCombined: isSell
              ? "-"
              : formatCombinedPnlDisplay(unrealPnl, unrealPct),
            realisedPNLCombined: isSell
              ? formatCombinedPnlDisplay(realisedPnl, realisedPct)
              : "-",
            days: resolveTxnDays({ ...t, metadata: t.metadata ?? t.meta }),
            cagr: t.cagr ?? h.cagr ?? resolveHoldingCagr(h),
          }),
        ),
      );
    }
  }
  return txns;
};

const normalizeHoldingRowForAll = (row: Record<string, any>) => {
  const normalized = normalizeUnrealizedHoldingRow({
    ...row,
    type: "Unrealized",
  });
  return {
    ...normalized,
    costValue:
      normalized.investedAmount ?? normalized.costValue ?? normalized.amount,
    currentValue:
      normalized.currentAmount ?? normalized.currentValue,
    gainLoss: normalized.gainLoss,
    gainLossPercentage: normalized.gainLossPercentage,
  };
};

const normalizeTxnRowForAll = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  const meta = row?.metadata ?? row?.meta ?? {};
  const pnl =
    row.realisedPnl ??
    row.realisedPnL ??
    row.realisedPNL ??
    row.realizedPnl ??
    row.realizedPNL ??
    row.realisedGainLoss ??
    row.realizedGainLoss;
  const pnlPct =
    row.realisedPNLPercentage ??
    row.realisedPnLPercentage ??
    row.realizedPNLPercentage ??
    row.realizedPnLPercentage;
  return {
    ...normalizeRealizedRow(row),
    type: formatReportTypeColumnLabel(
      isSell ? "Realized" : "Unrealized",
      isSell ? "Realized" : "Unrealized",
    ),
    costValue: isSell ? "-" : (row.amount ?? row.costValue),
    currentValue: isSell ? "-" : (row.currentAmount ?? row.currentValue),
    days: resolveTxnDays(row),
    gainLoss: pnl ?? "-",
    gainLossPercentage: pnlPct ?? "-",
    adjustedCostPrice:
      row.adjustedCostPrice ?? meta.adjustedCostPrice ?? row.adjCost,
  };
};

const mapTransactionDetailsRow = (row: Record<string, any>) => {
  const isSell = isSellTxn(row);
  return {
    ...row,
    transactiondate: formatEpochDate(
      row.transactiondate ?? row.transactionDate ?? row.date,
    ),
    schemeName: row.schemeName ?? row.scheme_name,
    folioNo: row.folioNo ?? row.folioNumber ?? row.folio,
    transactionType:
      row.transactionType ?? row.transType ?? (isSell ? "Sell" : "Buy"),
    currentDate: formatEpochDate(row.currentDate ?? Date.now()),
    amount: row.amount ?? row.netPurchase ?? row.costValue ?? row.investedAmount,
    currentValue: row.currentValue ?? row.currentAmount,
  };
};

const flattenSectorCapRows = (rows: any[]): any[] => {
  const out: any[] = [];
  for (const row of rows || []) {
    if (!row || typeof row !== "object") continue;
    if (Array.isArray(row.sectors)) {
      for (const s of row.sectors) {
        out.push({ ...s, memberName: row.memberName ?? s.memberName });
      }
    } else {
      out.push(row);
    }
  }
  return out;
};

const extractSifHoldings = (payload: any): any[] => {
  if (Array.isArray(payload?.holdings)) return payload.holdings;
  if (Array.isArray(payload?.data?.holdings)) return payload.data.holdings;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload)) return payload;
  return [];
};

const extractTotalHoldingInfo = (payload: any): Record<string, unknown> | null =>
  normalizeSifReportTotalHoldingInfo(
    (payload?.totalHoldingInfo ??
      payload?.totalHolding ??
      payload?.data?.totalHoldingInfo ??
      null) as Record<string, unknown> | null,
  );

export type ParsedSifReport = {
  rows: any[];
  totalHoldingInfo?: Record<string, unknown> | null;
  totalPages?: number;
  pageIndex?: number;
  totalRecords?: number;
};

export const parseSifReportResponse = (
  reportType: string,
  payload: any,
  userMeta: UserMeta,
  selectedUserIds: number[] = [],
): ParsedSifReport => {
  let rows: any[] = [];
  let totalHoldingInfo: Record<string, unknown> | null = null;
  let totalPages = 1;
  let pageIndex = 1;
  let totalRecords = 0;

  switch (reportType) {
    case "Holding": {
      const holdings = extractSifHoldings(payload);
      rows = filterPositiveUnits(holdings).map(normalizeUnrealizedHoldingRow);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) ?? rows.length;
      break;
    }
    case "Realized": {
      const holdings = extractSifHoldings(payload);
      rows = filterRealizedTxnRows(flattenSifHoldingsTransactions(holdings)).map(
        normalizeRealizedRow,
      );
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "All": {
      const holdings = extractSifHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      const unrealized = filterPositiveUnits(holdings).map(normalizeHoldingRowForAll);
      const realized = filterRealizedTxnRows(
        flattenSifHoldingsTransactions(holdings),
      ).map(normalizeTxnRowForAll);
      rows = [...unrealized, ...realized];
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "Transaction": {
      const holdings = extractSifHoldings(payload);
      totalHoldingInfo = extractTotalHoldingInfo(payload);
      rows = flattenSifHoldingsTransactions(holdings);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = rows.length;
      break;
    }
    case "Transaction report": {
      const dataRows = Array.isArray(payload?.response) ? payload.response : [];
      rows = dataRows.map(mapTransactionDetailsRow);
      totalPages = Number(payload?.totalPages) || 1;
      pageIndex = Number(payload?.pageIndex) || 1;
      totalRecords = Number(payload?.totalRecords) || rows.length;
      break;
    }
    case SIF_POINT_TO_POINT_REPORT: {
      rows = extractSifP2pRows(payload).map(mapSifP2pReportRow);
      totalHoldingInfo =
        payload?.cagr != null
          ? { cagr: payload.cagr }
          : payload?.data?.cagr != null
            ? { cagr: payload.data.cagr }
            : extractTotalHoldingInfo(payload);
      totalRecords = rows.length;
      break;
    }
    case SIF_RETURNS_REPORT: {
      rows = Array.isArray(payload)
        ? payload
        : Array.isArray(payload?.data)
          ? payload.data
          : [];
      rows = rows.map((row) => withTxnMetadataFields(row));
      rows = [...rows].sort(
        (a, b) =>
          Number(
            b.weeklyReturnPercentage ??
              b.weekelyReturnPercentage ??
              b.returnPercentage ??
              0,
          ) -
          Number(
            a.weeklyReturnPercentage ??
              a.weekelyReturnPercentage ??
              a.returnPercentage ??
              0,
          ),
      );
      break;
    }
    case "Sector report":
    case "Cap report": {
      const raw = Array.isArray(payload) ? payload : payload?.data ?? [];
      rows = flattenSectorCapRows(raw).map((row) => withTxnMetadataFields(row));
      if (reportType === "Sector report") {
        rows = [...rows].sort(
          (a, b) => Number(b.amount || 0) - Number(a.amount || 0),
        );
      }
      break;
    }
    default:
      rows = [];
  }

  return {
    rows: attachInvestorMeta(rows, userMeta, selectedUserIds),
    totalHoldingInfo,
    totalPages,
    pageIndex,
    totalRecords,
  };
};

export const mergeSifTransactionPages = (existing: any[], next: any[]) => {
  const seen = new Set(
    existing.map(
      (r) =>
        `${r.userId}-${r.schemeName}-${r.transactiondate ?? r.transactionDate}-${r.folioNo ?? r.folioNumber}-${r.units}`,
    ),
  );
  const merged = [...existing];
  for (const row of next) {
    const key = `${row.userId}-${row.schemeName}-${row.transactiondate ?? row.transactionDate}-${row.folioNo ?? row.folioNumber}-${row.units}`;
    if (!seen.has(key)) {
      seen.add(key);
      merged.push(row);
    }
  }
  return merged;
};
